import { hubConnection, type Connection } from 'signalr-no-jquery';
import {
  getUserSettings,
  handleUnauthorized,
  isLoggedIn,
  updateUserCredits,
  updateUserRewards,
  type CreditsData,
} from '@/api/auth';
import { api } from '@/api/client';
import { hostBaseUrl } from '@/api/config';
import { endpoints } from '@/api/endpoints';
import { queryClient } from '@/api/query-client';
import { queryKeys } from '@/api/query-keys';
import { getBetslipTicket, betslipReplace } from '@/betslip/betslip-store';
import {
  clearRewardFromTicket,
  removeRewardById,
  upsertPlayerReward,
} from '@/domain/rewards';
import { applyDepositBonusSettingsMessage } from './apply-deposit-bonus-settings';
import { publishFreeSpinsBonus } from './free-spins-notifications';
import {
  creditsFromPlayerMessage,
  depositBonusSettingsFromPlayerMessage,
  freeSpinsBonusFromPlayerMessage,
  parsePlayerSocketMessage,
  rewardsFromPlayerMessage,
} from './player-socket-message';
import { publishNewRewardToast } from './reward-notifications';

let activeConnection: Connection | null = null;
let activeGuid: string | null = null;
let starting = false;

function playerHubUrl(): string {
  return `${hostBaseUrl('users').replace(/\/+$/, '')}/signalr/`;
}

/** Absolute hub URL for origin checks (relative `/_api/...` resolves against the page). */
function absolutePlayerHubUrl(): string {
  if (typeof window === 'undefined') return playerHubUrl();
  return new URL(playerHubUrl(), window.location.origin).href;
}

/**
 * Classic SignalR WebSockets frequently fail with HTTP 400 when the page origin
 * differs from the users host (e.g. localhost → devapi). XHR transports still work
 * with CORS; same-origin hubs (Vite `/_api/users` proxy) can use WebSockets.
 */
function startOptions(): { withCredentials: false; transport?: string[] } {
  const options: { withCredentials: false; transport?: string[] } = {
    withCredentials: false,
  };

  if (typeof window === 'undefined') return options;

  try {
    const hubOrigin = new URL(absolutePlayerHubUrl()).origin;
    if (hubOrigin !== window.location.origin) {
      options.transport = ['serverSentEvents', 'longPolling'];
    }
  } catch {
    options.transport = ['serverSentEvents', 'longPolling'];
  }

  return options;
}

function sessionGuidMismatch(incoming: unknown): boolean {
  if (typeof incoming !== 'string' || !incoming) return false;
  const current = getUserSettings()?.SessionGuid;
  return typeof current === 'string' && current.length > 0 && current !== incoming;
}

async function refreshCreditsFromApi(machineGuid: string): Promise<void> {
  try {
    const { host, path } = endpoints.player.refreshCredits;
    const credits = await api.post<CreditsData>(host, path, {
      Value: machineGuid,
    });
    if (credits && typeof credits === 'object') {
      updateUserCredits(credits);
    }
  } catch {
    // Socket reconnect still works without a fresh REST balance.
  }
}

function handlePlayerMessage(raw: unknown): void {
  const message = parsePlayerSocketMessage(raw);
  if (!message) return;

  switch (message.Type) {
    case 'Logout':
      handleUnauthorized();
      break;
    case 'Login':
      // Another session for this machine — force logout when SessionGuid differs.
      if (sessionGuidMismatch(message.Value)) {
        handleUnauthorized();
      }
      break;
    case 'Credits':
    case 'ExternalBet':
    case 'ExternalWin':
    case 'ExternalBetBonus':
    case 'ExternalWinBonus':
    case 'TicketWin':
    case 'TicketCancel': {
      const credits = creditsFromPlayerMessage(message);
      if (credits) updateUserCredits(credits);
      break;
    }
    case 'Message': {
      const machineGuid = getUserSettings()?.Guid?.trim() ?? '';
      void queryClient.invalidateQueries({
        queryKey: queryKeys.player.messages(
          machineGuid ? { machineGuid } : undefined,
        ),
      });
      break;
    }
    case 'Reward': {
      applyRewardSocketMessage(message);
      break;
    }
    case 'DepositBonusSettings': {
      applyDepositBonusSettingsMessage(
        depositBonusSettingsFromPlayerMessage(message),
      );
      break;
    }
    case 'FreeSpinsBonus': {
      const freeSpins = freeSpinsBonusFromPlayerMessage(message);
      if (freeSpins) publishFreeSpinsBonus(freeSpins);
      break;
    }
    // TicketLost / BetSettings — handled when those features land.
    default:
      break;
  }
}

function applyRewardSocketMessage(
  message: NonNullable<ReturnType<typeof parsePlayerSocketMessage>>,
): void {
  const payload = rewardsFromPlayerMessage(message);
  const reward = payload?.Reward;
  if (!reward) return;

  const user = getUserSettings();
  if (!user) return;

  if (payload.Delete) {
    updateUserRewards(removeRewardById(user.Rewards, reward.Id));
    const ticket = getBetslipTicket();
    if (ticket.RewardId === reward.Id) {
      betslipReplace(clearRewardFromTicket(ticket));
    }
    return;
  }

  updateUserRewards(upsertPlayerReward(user.Rewards, reward));
  publishNewRewardToast(reward.Id);
}

function startConnection(guid: string): void {
  if (!activeConnection) return;

  activeConnection
    .start(startOptions())
    .done(() => {
      void activeConnection?.createHubProxy('PlayerSocket').invoke('Subscribe', guid);
    })
    .fail(() => {
      // Player socket is best-effort; REST session still works.
    });
}

/**
 * Opens the UsersAPI classic SignalR `PlayerSocket` for the signed-in machine.
 * Safe to call repeatedly — only one connection is kept.
 */
export function connectPlayerSocket(machineGuid: string): void {
  if (!machineGuid || !isLoggedIn()) return;
  if (activeGuid === machineGuid && activeConnection) return;
  if (starting) return;

  disconnectPlayerSocket();
  starting = true;
  activeGuid = machineGuid;

  try {
    const connection = hubConnection(playerHubUrl(), { useDefaultPath: false });
    const proxy = connection.createHubProxy('PlayerSocket');

    proxy.on('PlayerMessage', (message: unknown) => {
      handlePlayerMessage(message);
    });

    connection.disconnected(() => {
      if (!isLoggedIn() || activeGuid !== machineGuid) return;
      // Legacy reconnects and refreshes credits after a drop.
      startConnection(machineGuid);
      void refreshCreditsFromApi(machineGuid);
    });

    activeConnection = connection;
    startConnection(machineGuid);
  } catch {
    activeConnection = null;
    activeGuid = null;
  } finally {
    starting = false;
  }
}

/** Stops the player hub connection (logout / unauthorized). */
export function disconnectPlayerSocket(): void {
  const connection = activeConnection;
  activeConnection = null;
  activeGuid = null;
  starting = false;
  if (!connection) return;
  try {
    connection.stop();
  } catch {
    // Already torn down.
  }
}

/** Test seam. */
export function resetPlayerSocketState(): void {
  disconnectPlayerSocket();
}
